import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { lerp, precise, setCssVars } from "@/utils/utils"
import useFrame from "@/hooks/useFrame"
import { useIsDesktop } from "@/hooks/useDevice"
import SwipeCursor from "@/components/common/TakeOverCursor/SwipeCursor"

const DefaultContainer = styled.div`
	position: relative;
`

const TakeOverCursor = ({ Cursor = SwipeCursor, Container = DefaultContainer, children }) => {
	const ContainerRef = useRef()
	const CursorRef = useRef()
	const [sticky, setSticky] = useState(true)
	const [stale, setStale] = useState(true)
	const [isDown, setIsDown] = useState(false)
	const timeout = useRef()
	const isDesktop = useIsDesktop()
	const CursorPos = useRef({
		x: 0,
		y: 0,
	})
	const CursorPrevPos = useRef({
		x: 0,
		y: 0,
	})

	const handlePointerDown = useCallback(() => {
		setIsDown(true)
	}, [])

	const handlePointerUp = useCallback(() => {
		setIsDown(false)
	}, [])

	const handlePointerEnter = useCallback(() => {
		if (!isDesktop) return
		clearTimeout(timeout.current)
		setSticky(false)
		setStale(false)
	}, [isDesktop])

	const handlePointerLeave = useCallback(() => {
		if (!isDesktop) return
		setSticky(true)
		CursorPos.current = {
			x: 0,
			y: 0,
		}
		timeout.current = setTimeout(() => {
			setStale(true)
		}, 3000)
	}, [isDesktop])

	const handlePointerMove = useCallback(
		(e) => {
			if (!isDesktop) return
			const { clientX, clientY } = e
			const containerBound = ContainerRef.current.getBoundingClientRect()
			const center = {
				x: containerBound.x + containerBound.width / 2,
				y: containerBound.y + containerBound.height / 2,
			}

			CursorPos.current = {
				x: clientX - center.x,
				y: clientY - center.y,
			}
		},
		[isDesktop],
	)

	useEffect(() => {
		if (isDown) {
			window.addEventListener("pointerup", handlePointerUp)
		}
		return () => {
			if (isDown) {
				window.removeEventListener("pointerup", handlePointerUp)
			}
		}
	}, [isDown, handlePointerUp])

	useFrame(
		(elapsedTime, deltaTime) => {
			if (stale || !isDesktop) return
			const diff = Math.abs(CursorPrevPos.current.x - CursorPos.current.x)
			if (diff < 0.0001) return
			const lerpPower = sticky ? 7.5 : 15
			const pos = {
				x: precise(lerp(CursorPrevPos.current.x, CursorPos.current.x, lerpPower * deltaTime)),
				y: precise(lerp(CursorPrevPos.current.y, CursorPos.current.y, lerpPower * deltaTime)),
			}
			setCssVars(CursorRef.current, { x: `${pos.x}px`, y: `${pos.y}px` })
			CursorPrevPos.current = {
				x: pos.x,
				y: pos.y,
			}
		},
		[stale, sticky, isDesktop],
	)

	return (
		<Container
			ref={ContainerRef}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
			onPointerMove={handlePointerMove}
			onPointerDown={handlePointerDown}
		>
			<Cursor ref={CursorRef} isDown={isDown} className="take-over-cursor" />
			{children}
		</Container>
	)
}

export default TakeOverCursor
